import { DatePicker, LocalizationProvider } from '@mui/lab'
import AdapterDateFns from '@mui/lab/AdapterDateFns'
import { Box, Button, Container, FormControl, FormLabel, Tooltip, Typography } from '@mui/material'
import { CompanySelect, ConfirmDialog, Icon, SnackBarContext } from 'components'
import { TextField } from 'components/textField'
import { sv } from 'date-fns/locale'
import { inject, observer } from 'mobx-react'
import { Course, Step } from 'models'
import React from 'react'
import { useTranslation } from 'react-i18next'
import {  Pages } from 'routes'
import { AuthStore, RoutingStore } from 'stores'
import { DataStatus } from 'types'


export interface ICourseSettingsProps {
  course?:       Course,
  step?:         Step,
  routingStore?: RoutingStore,
  authStore?:    AuthStore,
}

export const CourseSettings = inject('routingStore', 'authStore')(observer((props: ICourseSettingsProps) => {
  const {routingStore, course, authStore} = props;
  const { t }     = useTranslation();
  const { toast } = React.useContext(SnackBarContext);
  const [startDate, setStartDate]      = React.useState<Date | null>(new Date());
  const [endDate,   setEndDate]        = React.useState<Date | null>(new Date());
  const [openConfirm,        setOpenConfirm]        = React.useState(false);
  const [openPublishConfirm, setOpenPublishConfirm] = React.useState(false);

  
  const buttonAction = React.useCallback(async () => {
    const old_status:    DataStatus = course.status;
    const target_status: DataStatus = course.status === DataStatus.Published ? DataStatus.Archived : DataStatus.Published;
    
    if(old_status === DataStatus.Draft && !openPublishConfirm) {
      setOpenPublishConfirm(true);
      return;
    }
    if(await course?.set('status', target_status, true)) toast(t('course_status_changed'), 'success');
    else {
      course?.set('status', old_status, false);
      console.error('Publish failed. Do something!');
      toast(t('toast_error_status_change_fail'), 'error');
    }
    setOpenPublishConfirm(false);
  }, [course, openPublishConfirm]);

  const buttonLabel = () => {
    if (course?.status === DataStatus.Draft)
      return t('publish');
    else if (course?.status === DataStatus.Published)
      return t('archive');
    else if (course?.status === DataStatus.Archived)
      return t('publish');
  };
  
  React.useEffect(() => {
    if (course) course?.loadDocuments();
  }, [course]);
  
  if (!course) return null;

  return (
    <Container maxWidth='md' sx={{display:'flex', flexDirection:'column'}}>
      <Typography variant='h4' marginBottom={4}>
        {t('course_settings')}
      </Typography>
      <FormControl
        sx     = {{ display: 'flex', flexDirection: 'column' }}
        margin = 'normal'
      >
        <FormLabel>{t('course_name')}</FormLabel>
        <TextField
          hiddenLabel
          debounced
          delay    = {300}
          value    = {course?.title || ''}
          variant  = 'outlined'
          onChange = {(e) => course.set('title', e.target.value, true)}
        />
      </FormControl>

      <FormControl fullWidth margin='normal'>
        <FormLabel>{t('course_description')}</FormLabel>
        <TextField
          hiddenLabel
          multiline
          debounced
          minRows     = '3'
          variant     = 'outlined'
          delay       = {300}
          placeholder = {t('course_description_placeholder')}
          value       = {course?.desc || ''}
          onChange    = {(e: React.ChangeEvent<HTMLTextAreaElement>) =>
            course.set('desc', e.target.value, true)
          }
        />
      </FormControl>

      <FormControl
        sx     = {{ display: 'flex', flexDirection: 'column' }}
        margin = 'normal'
      >
        <FormLabel>{t('category')}</FormLabel>
        <TextField
          hiddenLabel
          debounced
          delay      = {300}
          value      = {course?.category || ''}
          variant    = 'outlined'
          inputProps = {{ maxLength: 40 }}
          onChange   = {(e) => course.set('category', e.target.value, true)}
        />
      </FormControl>
      {/* <FormControl>
        <FormLabel>{t('athen_id')}</FormLabel>
        <TextField
          hiddenLabel
          debounced
          delay    = {300}
          value    = {course.athen_id || ''}
          variant  = 'outlined'
          onChange = {(
            e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
          ) => course.set('athen_id', e.target.value, true)}
        />
      </FormControl> */}
      <Box
        display        = 'flex'
        gap            = {3}
        flexDirection  = 'row'
        justifyContent = 'space-between'
      >
        <LocalizationProvider locale={sv} dateAdapter={AdapterDateFns}>
          <FormControl margin = 'normal' fullWidth>
            <FormLabel>{t('start_date')}</FormLabel>
            <DatePicker
              disabled
              mask     = "____-__-__"
              openTo   = 'year'
              value    = {startDate}
              onChange = {(startDate: React.SetStateAction<Date>) => {
                setStartDate(startDate);
              }}
              renderInput = {(params) => <TextField {...params} />}
            />
          </FormControl>
          <FormControl margin = 'normal' fullWidth>
            <FormLabel>{t('end_date')}</FormLabel>
            <DatePicker
              disabled
              mask     = "____-__-__"
              openTo   = 'year'
              value    = {endDate}
              onChange = {(endDate) => {
                setEndDate(endDate)
              }}
              renderInput = {(params) => <TextField {...params} />}
            />
          </FormControl>
        </LocalizationProvider>
      </Box>

      {!authStore.getCurUser.external_editor && 
      <FormControl margin = 'normal' fullWidth>
        <CompanySelect content={course} />
      </FormControl>
      
      }

      <Box display='flex' mt={4} justifyContent='space-between'>
        <Button
          onClick   = {() => setOpenConfirm(true)}
          color     = 'secondary'
          variant   = 'outlined'
          startIcon = {<Icon iconId='bin' />}
          disabled  = {!course?.canEdit}
        >
          {t('delete_course')}
        </Button>
        <Tooltip title={!course?.canPublish ? t('publish_course_disabled') : ''}>
        <Box>
        <Button
          onClick  = {buttonAction}
          disabled = {!course?.canPublish}
          variant  = 'contained'
        >
          {buttonLabel()} 
        </Button>
      </Box>
      </Tooltip>
      <ConfirmDialog
        title             = {t('confirm_publish_title') + '?'}
        open              = {openPublishConfirm}
        onClose           = {() => setOpenPublishConfirm(false)}
        onCancel          = {() => setOpenPublishConfirm(false)}
        content           = {t('confirm_publish_course_body')}
        confirmButtonText = {t('yes')}
        cancelButtonText  = {t('cancel')}
        onConfirm         = {buttonAction}
      />
      <ConfirmDialog
        title             = {t('confirm_delete_title') + '?'}
        open              = {openConfirm}
        onClose           = {() => setOpenConfirm(false)}
        onCancel          = {() => setOpenConfirm(false)}
        content           = {t('confirm_delete_course_body')}
        confirmButtonText = {t('yes')}
        cancelButtonText  = {t('cancel')}
        onConfirm         = {async () => {
          if (await course.remove())
            routingStore.push(Pages.Courses);
          else {
            toast(t('toast_error_remove_course'), 'error');
            console.error('Failed to remove course');
          }
        }}
      />
      </Box>
    </Container>
  );
}));
